import type { ProjectSettings, SdkUser } from '@dynamic-labs/sdk-api-core';
import * as z from 'zod/mini';

import { createStorageKeySchema } from '../../services/storage';
import { unverifiedWalletAccountSchema } from '../wallets/unverifiedWalletAccounts';
import { walletProviderKeyMapSchema } from '../wallets/walletProvider/walletProviderKeyMap/schema';

export const stateStorageKeySchema = createStorageKeySchema({
  key: 'state',
  schema: z.object({
    apiVersion: z.string(),
    prefetchedNonces: z.array(z.string()),
    prefetchedNoncesExpiration: z.nullable(z.number()),
    projectSettings: z.custom<ProjectSettings | null>(),
    unverifiedWalletAccounts: z.array(unverifiedWalletAccountSchema),
    user: z.custom<SdkUser | null>(),
    walletProviderKeyMap: walletProviderKeyMapSchema,
  }),
});

export const sessionStorageKeySchema = createStorageKeySchema({
  key: 'session',
  schema: z.object({
    /**
     * The current available captcha token.
     */
    captchaToken: z.nullable(z.string()),

    /**
     * The current available elevated access tokens.
     */
    elevatedAccessTokens: z.array(
      z.object({
        /**
         * Milliseconds since epoch of token expiration.
         */
        expiresAt: z.number(),
        /**
         * The scopes of the token.
         */
        scopes: z.array(z.string()),
        /**
         * Whether the token is single use.
         */
        singleUse: z.nullable(z.boolean()),
        /**
         * The token string.
         */
        token: z.string(),
      })
    ),

    /**
     * Legacy token will be undefined in environments with cookie based sessions.
     *
     * @deprecated Use `token` instead.
     */
    legacyToken: z.nullable(z.string()),

    /**
     * The current available MFA token.
     */
    mfaToken: z.nullable(z.string()),

    /**
     * Milliseconds since epoch of session expiration.
     */
    sessionExpiration: z.number(),

    /**
     * The current session keys.
     */
    sessionKeys: z.nullable(z.string()),

    /**
     * Token will be undefined in environments with cookie based sessions.
     */
    token: z.nullable(z.string()),
  }),
});
